import { useState } from 'react';
import { Link } from 'react-router-dom';
import OrderTimeline from './OrderTimeline';
import { paymentMethodLabel, paymentStatusLabel, type TrackedOrder } from '@/services/orderTracking';
import { downloadOrderReceipt } from '@/services/orderReceipt';
import { reopenSistecreditoFinancing } from '@/services/sistecredito';
import { PCP_OFFICIAL_WHATSAPP } from '@/services/finances';

interface OrderDetailProps {
  order: TrackedOrder;
  // Dato de titularidad con el que el cliente consultó su pedido (celular o
  // correo). Sirve para volver a pedir el enlace de financiamiento de forma
  // segura para el mismo pedido.
  contact?: string;
}

function formatDate(value: string | null): string {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

export default function OrderDetail({ order, contact }: OrderDetailProps) {
  const declined = order.financingDeclined;
  const badgeClass = declined
    ? 'bg-red-100 text-red-700'
    : order.isCancelled
      ? 'bg-red-100 text-red-700'
      : order.financing
        ? 'bg-amber-100 text-amber-700'
        : order.stageIndex >= 4
          ? 'bg-emerald-100 text-emerald-700'
          : order.status === 'pending_payment'
            ? 'bg-amber-100 text-amber-700'
            : 'bg-accent-100 text-accent-700';

  const statusText = declined
    ? 'Financiamiento rechazado'
    : order.financing
      ? 'En financiamiento'
      : order.statusLabel;

  const [reopenLoading, setReopenLoading] = useState(false);
  const [reopenError, setReopenError] = useState('');
  const [reopenNotice, setReopenNotice] = useState('');

  // Vuelve a pedir a Sistecrédito el enlace de su checkout para ESTE pedido
  // (retomar una solicitud que quedó pendiente, sin crear un pedido nuevo).
  async function handleReopen() {
    setReopenLoading(true);
    setReopenError('');
    setReopenNotice('');
    try {
      const result = await reopenSistecreditoFinancing({
        orderNumber: order.orderNumber,
        contact: contact ?? '',
      });
      if (result.redirectUrl) {
        window.open(result.redirectUrl, '_blank', 'noopener,noreferrer');
      } else {
        setReopenNotice(
          'Sistecrédito recibió tu solicitud, pero aún no devolvió el enlace de su checkout. Escríbenos por WhatsApp y te ayudamos a completarla.',
        );
      }
    } catch (err) {
      setReopenError(
        (err as Error)?.message ?? 'No pudimos reabrir el enlace de financiamiento.',
      );
    } finally {
      setReopenLoading(false);
    }
  }

  // Resumen del último intento de Sistecrédito (cuotas, ID de crédito y motivo
  // del rechazo) para que el cliente lo vea de un vistazo, igual que el panel.
  const attempt = order.sistecreditoAttempt;
  const showAttempt =
    Boolean(attempt) &&
    (attempt?.installments !== null ||
      Boolean(attempt?.creditId) ||
      Boolean(attempt?.rejectionReason) ||
      Boolean(attempt?.status));
  const attemptBadge =
    declined || attempt?.status === 'rejected'
      ? { label: 'Rechazado', className: 'bg-red-100 text-red-700', icon: 'ri-close-circle-line' }
      : attempt?.status === 'cancelled'
        ? { label: 'Cancelado / Error', className: 'bg-red-100 text-red-700', icon: 'ri-error-warning-line' }
        : attempt?.status === 'approved'
          ? { label: 'Aprobado', className: 'bg-emerald-100 text-emerald-700', icon: 'ri-checkbox-circle-line' }
          : { label: 'Pendiente', className: 'bg-amber-100 text-amber-700', icon: 'ri-time-line' };

  // Insignia por estado de un intento individual del historial de Sistecrédito.
  function entryBadge(status: string) {
    if (status === 'approved') {
      return { label: 'Aprobado', className: 'bg-emerald-100 text-emerald-700', icon: 'ri-checkbox-circle-line' };
    }
    if (status === 'rejected') {
      return { label: 'Rechazado', className: 'bg-red-100 text-red-700', icon: 'ri-close-circle-line' };
    }
    if (status === 'cancelled') {
      return { label: 'Cancelado / Error', className: 'bg-red-100 text-red-700', icon: 'ri-error-warning-line' };
    }
    return { label: 'Pendiente', className: 'bg-amber-100 text-amber-700', icon: 'ri-time-line' };
  }

  const waNumber = PCP_OFFICIAL_WHATSAPP.replace(/\D/g, '');
  const waMessage = `Hola PCP, quiero consultar el estado de mi pedido *${order.orderNumber}*.`;
  const waUrl = `https://wa.me/${waNumber}?text=${encodeURIComponent(waMessage)}`;

  return (
    <div className="space-y-5">
      {/* Encabezado del pedido */}
      <div className="bg-white rounded-2xl border border-background-200 p-5 md:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs text-foreground-500">Número de pedido</p>
            <p className="text-lg md:text-xl font-bold text-foreground-950">{order.orderNumber}</p>
            {order.createdAt && (
              <p className="text-xs text-foreground-500 mt-1">Realizado el {formatDate(order.createdAt)}</p>
            )}
          </div>
          <div className="flex flex-col items-end gap-2">
            <span className={`text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap ${badgeClass}`}>
              {statusText}
            </span>
            {order.production && (
              <span className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 whitespace-nowrap">
                Producto en producción
              </span>
            )}
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-background-100 flex flex-wrap items-center justify-between gap-3">
          {order.recipientName ? (
            <div className="flex items-center gap-2 text-sm text-foreground-600">
              <i className="ri-user-line text-foreground-400"></i>
              <span>{order.recipientName}</span>
            </div>
          ) : (
            <span />
          )}
          <button
            type="button"
            onClick={() => downloadOrderReceipt(order)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-accent-300 text-accent-700 text-sm font-semibold hover:bg-accent-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-download-2-line"></i>
            Descargar comprobante
          </button>
        </div>
      </div>

      {/* Aviso: financiamiento RECHAZADO por Sistecrédito */}
      {declined && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-5 md:p-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-close-circle-line text-red-600 text-xl"></i>
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-semibold text-red-800">Tu financiamiento fue rechazado</h2>
              <p className="text-sm text-red-700 mt-1 leading-relaxed">
                Sistecrédito no aprobó tu solicitud de compra a cuotas, por lo que el pedido{' '}
                <strong>{order.orderNumber}</strong> quedó cancelado y el stock se liberó. No se realizó
                ningún cobro. Puedes intentarlo de nuevo con otro método de pago.
              </p>
              <div className="flex flex-col sm:flex-row gap-2.5 mt-4">
                <Link
                  to="/checkout"
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-accent-500 text-white rounded-full font-semibold text-sm hover:bg-accent-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-refresh-line"></i>
                  Reintentar con otro método
                </Link>
                <a
                  href={waUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-white border border-red-300 text-red-700 rounded-full font-semibold text-sm hover:bg-red-100 transition-colors whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line"></i>
                  Escribir por WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resumen del último intento de Sistecrédito: cuotas, ID de crédito y
          motivo del rechazo, para que el cliente lo vea de un vistazo. */}
      {showAttempt && (
        <div
          className={`rounded-2xl border p-5 md:p-6 ${
            declined || attempt?.status === 'rejected'
              ? 'border-red-200 bg-red-50'
              : 'border-background-200 bg-white'
          }`}
        >
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h2 className="text-base font-semibold text-foreground-900 flex items-center gap-2">
              <i className="ri-hand-coin-line text-accent-600"></i>Tu financiamiento Sistecrédito
            </h2>
            <span
              className={`text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap inline-flex items-center gap-1.5 ${attemptBadge.className}`}
            >
              <i className={attemptBadge.icon}></i>
              {attemptBadge.label}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mt-4">
            <div className="flex justify-between gap-3 text-sm">
              <span className="text-foreground-500">Número de cuotas</span>
              <span className="font-medium text-foreground-900">
                {attempt?.installments !== null && attempt?.installments !== undefined
                  ? attempt.installments
                  : '—'}
              </span>
            </div>
            <div className="flex justify-between gap-3 text-sm">
              <span className="text-foreground-500">ID de crédito</span>
              <span className="font-medium text-foreground-900 break-all text-right">
                {attempt?.creditId || '—'}
              </span>
            </div>
          </div>
          {(declined || attempt?.status === 'rejected') && (
            <div className="mt-4 pt-3 border-t border-red-200">
              <p className="text-xs text-foreground-500 mb-0.5">Motivo del rechazo</p>
              <p className="text-sm text-red-700">
                {attempt?.rejectionReason || 'Sistecrédito no registró un motivo específico.'}
              </p>
            </div>
          )}
          {order.financing && (
            <p className="text-xs text-amber-700 mt-4 leading-relaxed">
              Tu solicitud está en proceso de aprobación por Sistecrédito. No necesitas hacer nada
              por ahora.
            </p>
          )}
        </div>
      )}

      {/* Historial COMPLETO de intentos de Sistecrédito (todos, no solo el último). */}
      {order.sistecreditoAttempts.length > 0 && (
        <div className="bg-white rounded-2xl border border-background-200 p-5 md:p-6">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h2 className="text-base font-semibold text-foreground-900 flex items-center gap-2">
              <i className="ri-history-line text-accent-600"></i>Historial de Sistecrédito
            </h2>
            <span className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 whitespace-nowrap">
              {order.sistecreditoAttempts.length}{' '}
              {order.sistecreditoAttempts.length === 1 ? 'intento' : 'intentos'}
            </span>
          </div>
          <ol className="mt-4 space-y-3">
            {order.sistecreditoAttempts.map((entry, index) => {
              const badge = entryBadge(entry.status);
              return (
                <li key={index} className="flex items-start gap-3">
                  <span
                    className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${badge.className}`}
                  >
                    <i className={badge.icon}></i>
                  </span>
                  <div className="flex-1 min-w-0 border border-background-100 rounded-lg p-3">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span
                        className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap ${badge.className}`}
                      >
                        <i className={badge.icon}></i>
                        {badge.label}
                      </span>
                      <span className="text-[11px] text-foreground-500 whitespace-nowrap">
                        {formatDate(entry.createdAt)}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 mt-2 text-xs">
                      <div className="flex justify-between gap-3">
                        <span className="text-foreground-500">Cuotas</span>
                        <span className="font-medium text-foreground-900">
                          {entry.installments !== null && entry.installments !== undefined
                            ? entry.installments
                            : '—'}
                        </span>
                      </div>
                      <div className="flex justify-between gap-3">
                        <span className="text-foreground-500">ID de crédito</span>
                        <span className="font-medium text-foreground-900 break-all text-right">
                          {entry.creditId || '—'}
                        </span>
                      </div>
                    </div>
                    {entry.rejectionReason && (
                      <div className="mt-2 pt-2 border-t border-background-100">
                        <p className="text-[11px] text-foreground-500 mb-0.5">Motivo del rechazo</p>
                        <p className="text-xs text-red-700">{entry.rejectionReason}</p>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {/* Línea de tiempo */}
      <div className="bg-white rounded-2xl border border-background-200 p-5 md:p-6">
        <h2 className="text-base font-semibold text-foreground-900 mb-5 flex items-center gap-2">
          <i className="ri-route-line text-accent-600"></i>Estado del pedido
        </h2>
        <OrderTimeline stages={order.stages} stageIndex={order.stageIndex} cancelled={order.isCancelled} />
        {order.financing && (
          <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
            <div className="flex items-start gap-2">
              <i className="ri-hand-coin-line text-amber-600 mt-0.5"></i>
              <p className="text-xs text-amber-800 leading-relaxed">
                Tu compra a cuotas está <strong>en proceso de financiamiento</strong>. Estamos esperando la aprobación de Sistecrédito; te avisaremos apenas se confirme.
              </p>
            </div>
            <div className="mt-3 pl-6">
              <button
                type="button"
                onClick={handleReopen}
                disabled={reopenLoading}
                className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-full font-semibold text-xs hover:bg-amber-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {reopenLoading ? (
                  <><i className="ri-loader-4-line animate-spin"></i>Generando enlace...</>
                ) : (
                  <><i className="ri-external-link-line"></i>Reabrir enlace de financiamiento</>
                )}
              </button>
              <p className="text-[11px] text-amber-700 mt-1.5">
                ¿Cerraste la solicitud antes de terminar? Genera el enlace de nuevo y complétala.
              </p>
              {reopenError && <p className="text-xs text-red-600 mt-1">{reopenError}</p>}
              {reopenNotice && <p className="text-xs text-amber-800 mt-1">{reopenNotice}</p>}
            </div>
          </div>
        )}
        {order.production && !order.isCancelled && (
          <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-2">
            <i className="ri-tools-line text-amber-600 mt-0.5"></i>
            <p className="text-xs text-amber-800 leading-relaxed">
              Uno de tus productos se está fabricando. Tiempo de entrega estimado de <strong>5-6 días hábiles</strong>.
            </p>
          </div>
        )}
      </div>

      {/* Productos */}
      <div className="bg-white rounded-2xl border border-background-200 p-5 md:p-6">
        <h2 className="text-base font-semibold text-foreground-900 mb-4 flex items-center gap-2">
          <i className="ri-shopping-bag-3-line text-accent-600"></i>Productos
        </h2>
        <div className="space-y-3">
          {order.items.map((item, index) => (
            <div key={`${item.productName}-${index}`} className="flex gap-3 pb-3 border-b border-background-100 last:border-0 last:pb-0">
              <div className="w-14 h-14 rounded-lg bg-background-100 flex-shrink-0 overflow-hidden flex items-center justify-center">
                {item.productImage ? (
                  <img src={item.productImage} alt={item.productName} className="w-full h-full object-top object-cover" title={`${item.productName} Paticas con Propósito`} />
                ) : (
                  <i className="ri-footprint-line text-foreground-400 text-xl"></i>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground-900 truncate">{item.productName}</p>
                {item.sizeLabel && <p className="text-xs text-foreground-500">Talla {item.sizeLabel}</p>}
                <p className="text-xs text-foreground-500 mt-0.5">Cantidad: {item.quantity}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm font-semibold text-foreground-950">${item.subtotal.toLocaleString()}</p>
                <p className="text-[10px] text-foreground-400">${item.unitPrice.toLocaleString()} c/u</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 pt-3 border-t border-background-200 space-y-1.5">
          <div className="flex justify-between text-sm">
            <span className="text-foreground-500">Valor de productos</span>
            <span className="font-medium text-foreground-900">${order.subtotal.toLocaleString()}</span>
          </div>
          {order.discountAmount > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-foreground-500">Descuento</span>
              <span className="font-medium text-red-600">-${order.discountAmount.toLocaleString()}</span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-foreground-500">Costo de envío</span>
            {order.shippingCost > 0 ? (
              <span className="font-medium text-foreground-900">${order.shippingCost.toLocaleString()}</span>
            ) : (
              <span className="font-medium text-emerald-600">Incluido</span>
            )}
          </div>
          <div className="flex justify-between text-base pt-2 border-t border-background-100">
            <span className="font-semibold text-foreground-950">Total</span>
            <span className="font-bold text-accent-600">${order.total.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Envío y pago */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="bg-white rounded-2xl border border-background-200 p-5">
          <h3 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2">
            <i className="ri-map-pin-line text-accent-600"></i>Entrega
          </h3>
          <p className="text-sm text-foreground-700">{order.shippingAddress || 'Dirección no registrada'}</p>
          <p className="text-xs text-foreground-500 mt-1">
            {[order.shippingCity, order.shippingDepartment].filter(Boolean).join(', ') || 'Ciudad no registrada'}
          </p>
        </div>
        <div className="bg-white rounded-2xl border border-background-200 p-5">
          <h3 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2">
            <i className="ri-bank-card-line text-accent-600"></i>Pago
          </h3>
          <p className="text-sm text-foreground-700">{paymentMethodLabel(order.paymentMethod)}</p>
          {order.payments.length > 0 ? (
            <ul className="mt-2 space-y-1">
              {order.payments.map((payment, index) => (
                <li key={index} className="text-xs text-foreground-500 flex items-center justify-between gap-2">
                  <span>${payment.amount.toLocaleString()}</span>
                  <span className={`font-medium ${payment.status === 'approved' ? 'text-emerald-600' : 'text-foreground-500'}`}>
                    {paymentStatusLabel(payment.status)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-foreground-400 mt-1">Pago pendiente de registro</p>
          )}
        </div>
      </div>

      {/* Ayuda */}
      <div className="bg-background-100 rounded-2xl border border-background-200 p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <i className="ri-whatsapp-line text-emerald-600 text-lg"></i>
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground-900">¿Tienes dudas sobre tu pedido?</p>
            <p className="text-xs text-foreground-500 mt-0.5">Escríbenos y te ayudamos con gusto.</p>
          </div>
        </div>
        <a
          href={waUrl}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-emerald-500 text-white rounded-full font-semibold text-sm hover:bg-emerald-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-whatsapp-line text-base"></i>
          Contactar
        </a>
      </div>
    </div>
  );
}